import React, { useCallback, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Button } from 'ksnm-common-ui/lib/Button';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { Select } from 'ksnm-common-ui/lib/Select';
import type { Dispatch, SetStateAction } from 'react';

import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';
import { organizationsStore } from '@stores/dictionaryStores/organizationsStore';
import { passingControlStore } from '@stores/passingControlStore';
import { publicationRegisterStore } from '@stores/publicationRegisterStore';

import styles from './Filter.module.scss';

interface FilterInterface {
  setOrganizationCode: Dispatch<SetStateAction<string>>;
  organizationCode: string;
  filters: { [key: string]: string | undefined };
  setFilters: Dispatch<SetStateAction<{ [index: string]: string | undefined }>>;
  tab: string;
}

export const Filter = observer(
  ({ setFilters, filters, organizationCode, setOrganizationCode, tab }: FilterInterface): JSX.Element => {
    const { publicationRegister } = publicationRegisterStore;
    const { controlReports } = passingControlStore;
    const { organizations } = organizationsStore;

    const dataSource = useMemo(
      () => (tab === 'publicationRegister' ? publicationRegister : controlReports),
      [controlReports, publicationRegister, tab],
    );

    useEffect(() => {
      const headOrganization = organizations.find((org) => org.head);

      if (headOrganization && headOrganization.code) {
        setOrganizationCode(headOrganization.code);

        const name = organizations?.find((org) => org.code === headOrganization.code)?.name;
        setFilters({ organization: name });
      }
    }, [organizations, setFilters, setOrganizationCode]);

    useEffect(() => {
      fetchReferences(['organizations']);
    }, []);

    const yearItems = useMemo(() => {
      const output: string[] = [];
      dataSource.forEach((company) => company?.children?.forEach((year) => year.name && output.push(year.name)));

      return output;
    }, [dataSource]);

    const groupItems = useMemo(() => {
      const output: string[] = [];
      dataSource.forEach(
        (company) =>
          company?.children?.forEach((year) => {
            if (filters.year && filters.year !== year.name) return;
            return year?.children?.forEach((group) => group.name && output.push(group.name));
          }),
      );
      return [...new Set(output)];
    }, [filters.year, dataSource]);

    const packageItems = useMemo(() => {
      let output: string[] = [];
      dataSource.forEach(
        (company) =>
          company?.children?.forEach(
            (year) =>
              year?.children?.forEach(
                (group) => group?.children?.forEach((packageItem) => packageItem.name && output.push(packageItem.name)),
              ),
          ),
      );
      if (filters.year) {
        output = output.filter((item) => item.includes(filters.year ?? ''));
      }

      if (filters.group) {
        const group = filters.group.split(' - ')[0];
        output = output.filter((item) => {
          const itemGroup = item?.substring(item?.lastIndexOf('_') + 1, item?.lastIndexOf('Y'));
          return itemGroup === group;
        });
      }

      return output;
    }, [filters.group, filters.year, dataSource]);

    const filtersValue = useMemo(
      () => [
        {
          label: 'Отчетный год',
          value: filters.year,
          options: yearItems,
          width: 160,
          name: 'year',
        },
        {
          label: 'Код группы отчетности',
          value: filters.group,
          options: groupItems,
          width: 220,
          name: 'group',
        },
        {
          label: 'Код пакета отчетов',
          value: filters.package,
          options: packageItems,
          width: 240,
          name: 'package',
        },
      ],
      [filters.group, filters.package, filters.year, groupItems, packageItems, yearItems],
    );

    const onChange = useCallback(
      (name, value) => {
        if (name === 'organization') {
          setOrganizationCode(value);

          const organizationName = organizations?.find((org) => org.code === value)?.name;
          setFilters(() => ({ organization: organizationName }));
        }

        if (name === 'year') setFilters((state) => ({ ...state, year: value, package: undefined, group: undefined }));
        if (name === 'group') setFilters((state) => ({ ...state, group: value, package: undefined }));
        if (name === 'package') setFilters((state) => ({ ...state, package: value }));
      },
      [organizations, setFilters, setOrganizationCode],
    );

    const handleResetFilters = useCallback(() => {
      setFilters({});
    }, [setFilters]);

    const filtersItems = useMemo(
      () =>
        filtersValue.map(({ name, label, options, value, width }) => (
          <div key={label} style={{ width }}>
            <Select
              items={options.map((item) => ({ name: item, id: item }))}
              placeholder={label}
              value={value}
              size="small"
              onChange={(val) => onChange(name, val)}
              showArrow
              showSearch={false}
              allowClear
            />
          </div>
        )),
      [filtersValue, onChange],
    );

    const disabled = useMemo(() => Object.keys(filters).length < 2, [filters]);

    return (
      <div className={styles.designFilters}>
        <div className={styles.filterItems}>
          <Select
            items={organizations}
            placeholder="Выберите организацию"
            value={organizationCode}
            size="small"
            onChange={(value) => onChange('organization', value)}
            showSearch={false}
            allowClear={false}
            className={styles.select}
          />
          {filtersItems}
        </div>

        <Button onClick={handleResetFilters} disabled={disabled} icon={<Icon name="stop" />}>
          Сбросить фильтры
        </Button>
      </div>
    );
  },
);
